import { Trashbin } from '@creditornot/cb-icons';
import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';

import { ConfirmModal } from 'common/components';
import { usePluralization } from 'common/hooks';
import useCurrentUser from 'common/hooks/useCurrentUser';
import { useBulkDeleteTracking } from 'modules/menu/hooks/telemetryHooks';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';
import { OfferingApiDeleteOffersPayload, OfferingApiError } from 'modules/shared-menu/types';

import ActionButton from './ActionButton';
import BulkActionErrorList from './BulkActionErrorList';
import { BulkActionModalTitle } from './BulkActionModalTitle';
import { useBatchDeleteOffers } from './useBatchDeleteOffers';

type BulkDeleteProps = {
  selectedIds: Set<string>;
};

export const BulkDelete = ({ selectedIds }: BulkDeleteProps) => {
  const intl = useIntl();
  const { formatPluralMessage } = usePluralization();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { data: user } = useCurrentUser();
  const { offers, updateSelectionData, selection } = useSelectionData();
  const scope = useScopeContext();
  const idsToDelete = useMemo(() => [...selectedIds], [selectedIds]);
  const { executeBatchDeleteOffers, isLoading } = useBatchDeleteOffers();
  const trackDeleteItems = useBulkDeleteTracking(idsToDelete);

  const onSuccess = useCallback(
    (idsOfSuccessfullyDeletedOffers: Set<string>) => {
      toast.success(
        formatPluralMessage({
          id: 'shared-menu.bulk-actions.toasts.delete.success-description',
          count: idsOfSuccessfullyDeletedOffers.size,
        }),
      );
      const updatedAllOffers = offers.filter(offer => !idsOfSuccessfullyDeletedOffers.has(offer.id));
      const updatedCategories = selection.categories.map(selectionCategory => {
        return {
          ...selectionCategory,
          offer_bindings: selectionCategory.offer_bindings?.filter(
            offerBinding => !idsOfSuccessfullyDeletedOffers.has(offerBinding.offer_id),
          ),
        };
      });
      updateSelectionData({ actionType: 'updateAllOffers', payload: updatedAllOffers });
      updateSelectionData({ actionType: 'updateCategories', payload: updatedCategories });
    },
    [formatPluralMessage, offers, selection, updateSelectionData],
  );

  const onError = useCallback(
    (errors: OfferingApiError[]) => {
      toast.error(
        <BulkActionErrorList
          description={intl.formatMessage({ id: 'shared-menu.bulk-actions.toasts.delete.error-description' })}
          errors={errors}
          allOffers={offers}
          selectionPrimaryLanguage={selection.primary_language}
        />,
        {
          duration: Infinity,
        },
      );
    },
    [intl, offers, selection.primary_language],
  );

  const handleOnConfirm = useCallback(async () => {
    const payload: OfferingApiDeleteOffersPayload = {
      request_scope: scope,
      author: {
        kind: 'user',
        id: user?._id.$oid ?? '',
      },
    };

    await executeBatchDeleteOffers({ payload, idsToDelete, onSuccess, onError });
    trackDeleteItems();
    setIsDeleteModalOpen(false);
  }, [executeBatchDeleteOffers, idsToDelete, scope, user?._id.$oid, trackDeleteItems, onSuccess, onError]);

  return (
    <>
      <ActionButton
        icon={<Trashbin />}
        onClick={() => setIsDeleteModalOpen(true)}
        iconButtonVariant="negative"
        tooltipTitle={<FormattedMessage id="menu.bulkActions.deleteItems" />}
      />

      {isDeleteModalOpen && (
        <ConfirmModal
          cancelText={<FormattedMessage id="state.cancel" />}
          confirmText={<FormattedMessage id="menu.bulkActions.deleteItems" />}
          confirmVariant="secondaryNegative"
          description={<FormattedMessage id="menu.bulkActions.deleteItemsDescription" />}
          header={
            <BulkActionModalTitle
              count={selectedIds.size}
              title={<FormattedMessage id="menu.bulkActions.deleteItems" />}
            />
          }
          loading={isLoading}
          onCancel={() => setIsDeleteModalOpen(false)}
          onConfirm={handleOnConfirm}
          open
        />
      )}
    </>
  );
};
